import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Loader2 } from 'lucide-react'

import {
  createShot,
  listScriptDocuments,
  listScriptEpisodes,
  type ScriptDocument,
  type ScriptEpisodeSummary,
} from '@/api/studio-production'
import { resolveApiErrorMessage } from '@/features/studio/lib/resolveApiError'
import {
  formatEpisodeLabel,
  groupEpisodesBySeason,
  resolveEpisodesPerSeason,
  usesSeasonSplit,
} from '@/features/studio/script/season'
import { ProtoBtn, ProtoDropdown, ProtoInput, ProtoModal, ProtoNumberInput, ProtoTabs } from '@/features/studio/components/ui-kit'

export function ShotCreateModal({
  productionId,
  defaultScriptId,
  onClose,
  onCreated,
}: {
  productionId: string
  defaultScriptId?: number
  onClose: () => void
  onCreated: () => void
}) {
  const { t } = useTranslation()
  const [scripts, setScripts] = useState<ScriptDocument[]>([])
  const [episodes, setEpisodes] = useState<ScriptEpisodeSummary[]>([])
  const [episodeTarget, setEpisodeTarget] = useState(1)
  const [episodesPerSeason, setEpisodesPerSeason] = useState(0)
  const [scriptId, setScriptId] = useState(defaultScriptId ?? 0)
  const [seasonTab, setSeasonTab] = useState('1')
  const [episodeNo, setEpisodeNo] = useState(1)
  const [sceneNo, setSceneNo] = useState(1)
  const [description, setDescription] = useState('')
  const [durationSec, setDurationSec] = useState(3)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    ;(async () => {
      setLoading(true)
      try {
        const res = await listScriptDocuments(productionId, { limit: 50, offset: 0 })
        if (cancelled) return
        setScripts(res.items)
        const sid = defaultScriptId ?? res.items[0]?.id ?? 0
        setScriptId(sid)
      } catch (e) {
        if (!cancelled) setError(resolveApiErrorMessage(e, 'shots.createFailed'))
      } finally {
        if (!cancelled) setLoading(false)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [productionId, defaultScriptId])

  useEffect(() => {
    if (!scriptId) return
    let cancelled = false
    ;(async () => {
      try {
        const res = await listScriptEpisodes(productionId, scriptId)
        if (cancelled) return
        setEpisodes(res.items.filter((ep) => ep.has_body))
        setEpisodeTarget(res.episode_target)
        setEpisodesPerSeason(res.episodes_per_season)
        if (res.items.length > 0) {
          const first = res.items.find((ep) => ep.has_body) ?? res.items[0]
          setEpisodeNo(first.episode_no)
          const perSeason = resolveEpisodesPerSeason(res.episodes_per_season, res.episode_target)
          if (usesSeasonSplit(res.episodes_per_season, res.episode_target)) {
            setSeasonTab(String(Math.floor((first.episode_no - 1) / perSeason) + 1))
          }
        }
      } catch (e) {
        if (!cancelled) setError(resolveApiErrorMessage(e, 'shots.loadFailed'))
      }
    })()
    return () => {
      cancelled = true
    }
  }, [productionId, scriptId])

  const seasonGroups = useMemo(
    () => groupEpisodesBySeason(episodes, episodesPerSeason, episodeTarget),
    [episodes, episodesPerSeason, episodeTarget],
  )

  const showSeasonTabs = usesSeasonSplit(episodesPerSeason, episodeTarget) && seasonGroups.length > 1
  const activeSeasonEpisodes = showSeasonTabs
    ? (seasonGroups.find((g) => g.seasonNo === Number(seasonTab))?.items ?? [])
    : episodes

  const episodeOptions = activeSeasonEpisodes.map((ep) => ({
    value: String(ep.episode_no),
    label: `${formatEpisodeLabel(ep.episode_no, episodesPerSeason, episodeTarget)}${ep.title?.trim() ? ` · ${ep.title.trim()}` : ''}`,
  }))

  async function handleSubmit() {
    if (!scriptId || !episodeNo) return
    setSaving(true)
    setError(null)
    try {
      await createShot(productionId, {
        script_id: scriptId,
        episode_no: episodeNo,
        scene_no: sceneNo,
        description: description.trim() || undefined,
        duration_sec: durationSec,
      })
      onCreated()
      onClose()
    } catch (e) {
      setError(resolveApiErrorMessage(e, 'shots.createFailed'))
    } finally {
      setSaving(false)
    }
  }

  return (
    <ProtoModal
      open
      onClose={onClose}
      title={t('shots.createModalTitle')}
      footer={
        <>
          <ProtoBtn size="sm" variant="secondary" onClick={onClose}>
            {t('common.cancel')}
          </ProtoBtn>
          <ProtoBtn size="sm" disabled={saving || loading || !scriptId} onClick={() => void handleSubmit()}>
            {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : null}
            {t('shots.add')}
          </ProtoBtn>
        </>
      }
    >
      <div className="space-y-4">
        {loading ? (
          <div className="flex justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin text-indigo-400" />
          </div>
        ) : (
          <>
            <div>
              <label className="mb-1 block text-xs text-zinc-500">{t('shots.fieldScript')}</label>
              <ProtoDropdown
                value={String(scriptId || '')}
                onValueChange={(v) => setScriptId(Number(v))}
                options={scripts.map((doc, i) => ({
                  value: String(doc.id),
                  label: doc.title?.trim() || t('script.unnamedScript', { n: i + 1 }),
                }))}
              />
            </div>

            {showSeasonTabs ? (
              <ProtoTabs
                tabs={seasonGroups.map((g) => ({
                  id: String(g.seasonNo),
                  label: t('shots.seasonTab', { n: g.seasonNo }),
                }))}
                active={seasonTab}
                onChange={setSeasonTab}
              />
            ) : null}

            <div>
              <label className="mb-1 block text-xs text-zinc-500">{t('shots.fieldEpisode')}</label>
              <ProtoDropdown
                value={String(episodeNo)}
                onValueChange={(v) => setEpisodeNo(Number(v))}
                options={episodeOptions}
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="mb-1 block text-xs text-zinc-500">{t('shots.fieldScene')}</label>
                <ProtoNumberInput min={1} max={9999} value={sceneNo} onValueChange={setSceneNo} />
              </div>
              <div>
                <label className="mb-1 block text-xs text-zinc-500">{t('shots.col.duration')}</label>
                <ProtoNumberInput min={1} max={600} value={durationSec} onValueChange={setDurationSec} />
              </div>
            </div>

            <div>
              <label className="mb-1 block text-xs text-zinc-500">{t('shots.col.description')}</label>
              <ProtoInput
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder={t('shots.descriptionPlaceholder')}
              />
            </div>
          </>
        )}

        {error ? <p className="text-xs text-rose-300">{t(error, { defaultValue: error })}</p> : null}
      </div>
    </ProtoModal>
  )
}
